/**
 * Self-service "gegevens controleren": klant corrigeert zelf zijn ophaal-/
 * afleveradres en (optioneel) mobiele nummer. Eén herbruikbaar blok dat in
 * beide relevante fasen leeft:
 *   - KiesAfspraakView (vóór inplannen) — alleen adres, met regio-hint.
 *   - GeplandView (ná inplannen)        — adres + telefoon, met gelockte-route-hint.
 *
 * Belvolume-reductie: een fout-maar-geldig huisnummer of een verkeerd nummer is
 * door niemand anders te detecteren (het geocodeert/valideert prima); de klant is
 * de enige bron van waarheid. We roepen elk endpoint alleen aan als dat veld
 * echt is gewijzigd — zo pint een telefoon-only correctie nooit per ongeluk een
 * adres-override vast.
 */
import { useState } from 'react'
import { Card } from './UI'
import { updateAdres, updateTelefoon } from '../api'
import { AdresVelden, useAdresForm } from './AdresVelden'

/** Lichte client-side check; Salesforce valideert/normaliseert definitief. */
function looksLikeMobile(raw: string): boolean {
  const s = raw.replace(/[^\d+]/g, '')
  if (/^0?6\d{8}$/.test(s)) return true       // 06xxxxxxxx of 6xxxxxxxx
  if (/^\+316\d{8}$/.test(s)) return true     // +316xxxxxxxx
  if (/^00316\d{8}$/.test(s)) return true     // 00316xxxxxxxx
  if (/^\+\d{8,15}$/.test(s)) return true     // buitenlands E.164
  return false
}

interface GegevensControlerenProps {
  token: string
  /** Welk adres bewerkt wordt — volgt uit de orderfase. */
  kant: 'pickup' | 'delivery'
  huidigAdres?: string
  huidigMobiel?: string
  /** Toon + sta het mobiele-nummer-veld toe (GeplandView). */
  toonTelefoon?: boolean
  /** Rustige hint: rit staat al klaar op een gelockte route. */
  routeGelockt?: boolean
  /** Hint dat een andere regio de beschikbare momenten aanpast (kies-fase). */
  regioHint?: boolean
  /** Geslaagd — parent ververst de order (+ eventuele eigen side-effects). */
  onOpgeslagen?: () => void
  onAnnuleer?: () => void
}

export function GegevensControleren({
  token,
  kant,
  huidigAdres,
  huidigMobiel,
  toonTelefoon = false,
  routeGelockt = false,
  regioHint = false,
  onOpgeslagen,
  onAnnuleer,
}: GegevensControlerenProps) {
  const adres = useAdresForm(huidigAdres)
  const [mobiel, setMobiel] = useState(huidigMobiel || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isPickup = kant === 'pickup'

  async function handleSave() {
    const adresGewijzigd = adres.isGewijzigd()
    const mobielTrim = mobiel.trim()
    const mobielGewijzigd = toonTelefoon && mobielTrim !== (huidigMobiel || '').trim()

    if (!adresGewijzigd && !mobielGewijzigd) {
      // Niets veranderd — gewoon sluiten zonder onnodige writes.
      onAnnuleer?.()
      return
    }

    // Validatie alleen voor de velden die we daadwerkelijk gaan opslaan.
    if (adresGewijzigd) {
      const fout = adres.valideer()
      if (fout) {
        setError(fout)
        return
      }
    }
    if (mobielGewijzigd && mobielTrim && !looksLikeMobile(mobielTrim)) {
      setError('Vul een geldig mobiel nummer in (bijvoorbeeld 06 12345678)')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      if (adresGewijzigd) {
        const d = adres.delen()
        const result = await updateAdres({
          token,
          straat: d.toevoeging ? `${d.straat} ${d.toevoeging}` : d.straat,
          postcode: d.postcode,
          plaats: d.plaats,
        })
        if (!result.success) {
          throw new Error(result.message || 'Er ging iets mis bij het adres')
        }
      }
      if (mobielGewijzigd && mobielTrim) {
        const result = await updateTelefoon({ token, mobiel: mobielTrim })
        if (!result.success) {
          throw new Error(result.message || 'Er ging iets mis bij het telefoonnummer')
        }
      }
      onOpgeslagen?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="-mt-sm mb-lg">
      <p className="section-label mb-md">
        {toonTelefoon
          ? 'Adres en telefoonnummer controleren'
          : isPickup
            ? 'Ophaaladres wijzigen'
            : 'Afleveradres wijzigen'}
      </p>

      <div className="space-y-sm">
        <AdresVelden adres={adres} disabled={isSaving} />

        {/* Telefoonnummer (alleen ná inplannen) */}
        {toonTelefoon && (
          <div>
            <label className="block text-body-sm text-ink-muted mb-xs">Mobiel nummer</label>
            <input
              type="tel"
              value={mobiel}
              onChange={(e) => setMobiel(e.target.value)}
              placeholder="06 12345678"
              className="input w-full"
              autoComplete="tel"
              disabled={isSaving}
            />
            <p className="mt-xs text-[12px] text-ink-faint">
              Zo kan de chauffeur je bereiken als dat nodig is.
            </p>
          </div>
        )}

        {regioHint && (
          <p className="text-[13px] text-ink-faint">
            Bij een andere regio passen de beschikbare momenten zich automatisch aan.
          </p>
        )}

        {routeGelockt && (
          <p className="text-[13px] text-ink-faint">
            We staan al klaar voor je rit. Wijzig je iets, dan passen we het meteen aan.
          </p>
        )}

        {error && (
          <p className="text-body-sm text-red-600">{error}</p>
        )}

        <div className="flex gap-sm pt-xs">
          <button
            type="button"
            onClick={() => onAnnuleer?.()}
            disabled={isSaving}
            className="btn-outline flex-1"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || adres.isLookingUp}
            className="btn-primary flex-1"
          >
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </Card>
  )
}
